import { ArrowRight, LockKeyhole, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { t } from '../i18n';
import { loadPopularSubjects } from '../services/popular-subjects';
import { setAccessToken, $japaneseAnimeOnly, $showNsfw } from '../store/app.store';
import { useApp } from '../components/app-context';
import { Button } from '../components/button';
import { Input } from '../components/input';
import { useStore } from '../hooks/use-store';
import { safeUrl } from '../utils/url';

const POSTER_ROWS = 5;
const POSTERS_PER_ROW = 20;

const preloadPoster = (url: string): Promise<string | undefined> =>
  new Promise((resolve) => {
    const image = new Image();
    image.referrerPolicy = 'no-referrer';
    image.onload = () => resolve(url);
    image.onerror = () => resolve(undefined);
    image.src = url;
  });

export const LoginPage = ({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) => {
  const { api, bgm, showToast } = useApp();
  const japaneseAnimeOnly = useStore($japaneseAnimeOnly);
  const showNsfw = useStore($showNsfw);
  const [loading, setLoading] = useState(false);
  const [posterUrls, setPosterUrls] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setPosterUrls([]);
    void loadPopularSubjects(bgm, { japaneseAnimeOnly, showNsfw })
      .then(async (subjects) => {
        const urls = [
          ...new Set(
            subjects.flatMap((subject) => {
              const url = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
              return url ? [url] : [];
            }),
          ),
        ];
        const loadedUrls = (await Promise.all(urls.map(preloadPoster))).filter(
          (url): url is string => url !== undefined,
        );
        if (!cancelled) {
          setPosterUrls(loadedUrls);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPosterUrls([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [bgm, japaneseAnimeOnly, showNsfw]);

  const posterRows = useMemo(
    () =>
      Array.from({ length: POSTER_ROWS }, (_, row) =>
        posterUrls.length
          ? Array.from({ length: POSTERS_PER_ROW }, (_, index) => posterUrls[(index + row * 3) % posterUrls.length]!)
          : [],
      ),
    [posterUrls],
  );

  return (
    <section className="login-view">
      {posterUrls.length > 0 ? (
        <div className="login-poster-wall" aria-hidden="true">
          {posterRows.map((posters, row) => (
            <div className="login-poster-row min-h-0 flex-1 overflow-hidden bg-transparent" key={row}>
              <div className="login-poster-track">
                {[0, 1].map((copy) => (
                  <div className="login-poster-sequence flex h-full w-max gap-1 bg-transparent" key={copy}>
                    {posters.map((url, index) => (
                      <img src={url} alt="" decoding="async" referrerPolicy="no-referrer" key={`${copy}-${index}`} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="noise" />
      <div className="login-card">
        <h1>
          {t('welcome')} <span>BGMer</span>
        </h1>
        <p className="login-copy">{t('loginDescription')}</p>
        <form
          className="grid gap-4.5"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            setLoading(true);
            try {
              const result = await api<{ accessToken: string }>('/api/user/login', {
                method: 'POST',
                body: JSON.stringify({ name: data.get('name'), password: data.get('password') }),
              });
              setAccessToken(result.accessToken);
              await onAuthenticated();
            } catch (error) {
              showToast(error instanceof Error ? error.message : String(error), 'error');
            } finally {
              setLoading(false);
            }
          }}
        >
          <Input label={t('username')} icon={<UserRound />} name="name" autoComplete="username" required />
          <Input
            label={t('password')}
            icon={<LockKeyhole />}
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
          <Button variant="primary" type="submit" disabled={loading}>
            <span>{t('login')}</span>
            <ArrowRight />
          </Button>
        </form>
      </div>
    </section>
  );
};
